import { Icon, deviceDetector } from "@ospweb/sdk-ui";

import { Empty } from "./Empty";
import { I18nUtil } from "@utils/i18n";
import { MenuSearchModalCreateAble } from "../../Menu/MenuSearchModal";
import type { PortfolioStatsProps } from "./types";
import React from "react";
import { RightContent } from "./RightContent";
import { StatSection } from "./StatSection";
import { TopPerformerLeftContent } from "./TopPerformerLeftContent";
import Translations from "@translate/components/Portfolio/PortfolioStats/PortfolioStats";
import { ValueLeftContent } from "./ValueLeftContent";
import { PortfolioAskEdAction } from "../PortfolioAskEdAction";
import { cn } from "utils";
import { useIsPc } from "@/src/hooks";
import { useMemoizedFn } from "ahooks";
import { usePortfolioHelp } from "@hooks/usePortfolioHelp";

const I18n = I18nUtil.createScoped(Translations);

/**
 * Portfolio Stats 统计摘要组件
 * 显示投资组合的关键统计数据
 */
export const PortfolioStats: React.FC<PortfolioStatsProps> = ({
  value = "",
  valueChange = "",
  valueChangePercent = "",
  totalPnl = "",
  totalPnlPercent = "",
  todayPnl = "",
  todayPnlPercent = "",
  topPerformerSymbol = "",
  topPerformerIcon,
  topPerformerPercent = "",
  valueIsPositive,
  pnlIsPositive,
  performanceIsPositive,
  className,
  isLoading = false,
  isEmpty,
  showAddTransactionButton,
  accountType,
  onAskEdClick,
}) => {
  const isMobile = deviceDetector.isMobile();
  const [clientIsPc] = useIsPc();
  const isPC = clientIsPc && !isMobile;
  const { shouldShowChart, shouldShowAddAssets, showChart } =
    usePortfolioHelp();

  // 根据 accountType 动态显示标题
  const getPnlTitle = () => {
    if (accountType === "CASH") {
      return I18n.t("Today PNL");
    } else {
      return I18n.t("Total PnL");
    }
  };

  // 根据 accountType 动态获取 PnL 值
  const getPnlValue = () => {
    if (isEmpty) {
      return "--";
    }
    if (accountType === "CASH") {
      return todayPnl; // CASH 优先使用 todayPnl
    } else {
      return totalPnl; // FULL 类型优先使用 totalPnl
    }
  };

  // 根据 accountType 动态获取 PnL 百分比
  const getPnlPercent = () => {
    if (accountType === "CASH") {
      return todayPnlPercent;
    } else {
      // FULL
      return totalPnlPercent;
    }
  };
  const handleAddAssets = useMemoizedFn(async () => {
    await MenuSearchModalCreateAble({
      type: "portfolio",
    });
  });

  // 根据 accountType 动态获取 PnL 是否为正值
  const getPnlIsPositive = () => {
    const percent = getPnlPercent();
    if (percent) {
      return (
        percent.startsWith("+") ||
        (percent !== "--" && !percent.startsWith("-"))
      );
    }
    return pnlIsPositive;
  };

  /** Figma Value/Row：饼图 icon 缩小（size-9 / icon 20）+ Ask Ed 并排 */
  const renderValueTrailingActions = () => (
    <div className="flex shrink-0 items-center gap-2">
      {shouldShowChart && (
        <button
          type="button"
          aria-label="Portfolio distribution chart"
          data-openreplay-label="Portfolio Stats · Chart"
          id="btn-portfolio-stats"
          className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-2 bg-background_brand_overlay10 p-1.5"
          onClick={showChart}
          data-tracking-scope="hide-chart"
        >
          <Icon
            name={"plus-chart"}
            size={20}
            className="text-content_primary size-5"
          />
        </button>
      )}
      {onAskEdClick && <PortfolioAskEdAction onClick={onAskEdClick} />}
      {shouldShowAddAssets && (
        <button
          type="button"
          aria-label="Add assets"
          data-openreplay-label="Portfolio Stats · Add assets"
          id="btn-portfolio-stats-2"
          className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-2 bg-background_brand_overlay10 p-1.5 text-content_primary"
          onClick={handleAddAssets}
          data-tracking-scope="add-assets"
        >
          <Icon name="plus" size={20} />
        </button>
      )}
    </div>
  );

  const renderDesktopContent = () => (
    <div
      className={cn(
        "flex gap-4 h-full px-5 py-4 max-md:flex-col",
        isEmpty && !isLoading && "pb-0 mb-[2px]",
      )}
    >
      <StatSection
        title={I18n.t("Value")}
        leftContent={
          <div className="flex min-w-0 w-full items-center justify-between gap-2">
            <ValueLeftContent
              value={value}
              changeAmount={isEmpty ? undefined : valueChange}
              className="min-w-0"
            />
            {renderValueTrailingActions()}
          </div>
        }
        rightContent={
          !isEmpty && valueChangePercent ? (
            <RightContent
              percent={valueChangePercent}
              isPositive={valueIsPositive}
            />
          ) : undefined
        }
        className="flex-1"
        isLoading={isLoading}
      />
      <StatSection
        title={I18n.t("Top Performer")}
        leftContent={
          <TopPerformerLeftContent
            symbol={isEmpty ? "--" : topPerformerSymbol}
            icon={!isEmpty ? topPerformerIcon : undefined}
          />
        }
        rightContent={
          !isEmpty && topPerformerPercent ? (
            <RightContent
              percent={topPerformerPercent}
              isPositive={performanceIsPositive}
            />
          ) : undefined
        }
        className="flex-1"
        isLoading={isLoading}
      />
    </div>
  );

  const renderMobileSkeleton = () => (
    <div className="flex flex-col gap-2 md:hidden">
      <div className="h-9 w-[255px] rounded-[4px] bg-background_white_overlay5 animate-pulse" />
      <div className="flex flex-col gap-2">
        <div className="h-4 w-[155px] rounded-[4px] bg-background_white_overlay5 animate-pulse" />
        <div className="h-4 w-[155px] rounded-[4px] bg-background_white_overlay5 animate-pulse" />
      </div>
    </div>
  );

  const renderMobileContent = () => {
    if (isLoading) {
      return renderMobileSkeleton();
    }

    const changeAmount = isEmpty ? undefined : valueChange;

    return (
      <div className="flex flex-col gap-4 border-px py-4 px-3 border-border_white_transparent10 rounded-3 relative md:hidden">
        {/* Figma Value/Row：总额左 · 饼图+Ask Ed 右 */}
        <div className="flex items-center justify-between gap-2">
          <p className="headline_regular_l text-content_primary tracking-[-1px] min-w-0 truncate">
            {value || "--"}
          </p>
          {renderValueTrailingActions()}
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1 min-w-0">
            <p className="body_m text-content_tertiary shrink-0 mr-1">
              {I18n.t("24h")}
            </p>
            {changeAmount && (
              <p className="body_medium_m text-content_primary min-w-0">
                {changeAmount}
              </p>
            )}
            {!isEmpty && valueChangePercent && (
              <RightContent
                percent={valueChangePercent}
                isPositive={valueIsPositive}
              />
            )}
          </div>
          {accountType !== "SPOT" && (
            <div className="flex items-center gap-1 min-w-0">
              <p className="body_m text-content_tertiary shrink-0 mr-1">
                {getPnlTitle()}
              </p>
              <p className="body_medium_m text-content_primary min-w-0">
                {getPnlValue()}
              </p>
              {!isEmpty && getPnlPercent() && (
                <RightContent
                  percent={getPnlPercent()}
                  isPositive={getPnlIsPositive()}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const shouldUseMobileLayout = !isPC;

  return (
    <div
      className={cn(
        "flex flex-col",
        shouldUseMobileLayout
          ? "gap-2 rounded-none border-0 p-0"
          : "rounded-xl border border-solid border-border_solid",
        className,
      )}
    >
      {shouldUseMobileLayout ? renderMobileContent() : renderDesktopContent()}
      {showAddTransactionButton && <Empty />}
    </div>
  );
};
